import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

const DEVICE_ID_KEY = 'device_id';

export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
    logger.debug('Generated new device_id');
  }
  return id;
}

export async function registerDevice(userId: string, publicKeyBase64: string) {
  const deviceId = getDeviceId();

  // Clear is_current on all user devices
  await (supabase as any)
    .from('user_devices')
    .update({ is_current: false })
    .eq('user_id', userId);

  // Check if user is admin — admins always get trusted devices
  const { data: adminRole } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', userId)
    .eq('role', 'admin')
    .maybeSingle();
  const isAdmin = !!adminRole;

  // Check if this specific device already exists
  const { data: existing } = await (supabase as any)
    .from('user_devices')
    .select('id, is_trusted')
    .eq('user_id', userId)
    .eq('device_id', deviceId)
    .maybeSingle();

  if (existing) {
    const updates: any = {
      public_key: publicKeyBase64,
      is_current: true,
      last_seen: new Date().toISOString(),
    };
    if (isAdmin && !existing.is_trusted) {
      updates.is_trusted = true;
    }
    const { error } = await (supabase as any)
      .from('user_devices')
      .update(updates)
      .eq('id', existing.id);
    if (error) logger.error('Device update failed:', error);
  } else {
    const { count } = await (supabase as any)
      .from('user_devices')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    const isFirstDevice = (count ?? 0) === 0;

    const { error } = await (supabase as any)
      .from('user_devices')
      .insert({
        user_id: userId,
        device_id: deviceId,
        device_name: 'Web Device',
        device_type: 'web',
        public_key: publicKeyBase64,
        is_current: true,
        is_trusted: isFirstDevice || isAdmin,
        last_seen: new Date().toISOString(),
      });
    if (error) logger.error('Device registration failed:', error);
  }
}

/** Returns true if this device is still registered for the user */
export async function verifyDevice(userId: string): Promise<boolean> {
  const deviceId = getDeviceId();
  const { data } = await (supabase as any)
    .from('user_devices')
    .select('id')
    .eq('user_id', userId)
    .eq('device_id', deviceId)
    .maybeSingle();
  return !!data;
}
